<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <base-widget
    icon="clock-fill"
    :loading="isLoading"
    :title="$t('dashboard.time_at_work')"
  >
    <div class="orangehrm-attendance-card">
      <div class="orangehrm-attendance-card-profile">
        <div class="orangehrm-attendance-card-profile-image">
          <img
            alt="profile picture"
            class="employee-image"
            :src="`../pim/viewPhoto/empNumber/${empNumber}`"
          />
        </div>
        <div class="orangehrm-attendance-card-profile-record">
          <oxd-text tag="p" class="orangehrm-attendance-card-state">
            {{ lastState }}
          </oxd-text>
          <oxd-text tag="p" class="orangehrm-attendance-card-details">
            {{ lastRecord }}
          </oxd-text>
        </div>
      </div>
      <div class="orangehrm-attendance-card-times">
        <div class="orangehrm-attendance-card-stat">
          <oxd-text tag="span" class="orangehrm-attendance-card-stat-label">
            {{ $t('attendance.total_time') }}
          </oxd-text>
          <oxd-text tag="span" class="orangehrm-attendance-card-stat-value">
            {{ formatTime(dayGross) }}
          </oxd-text>
        </div>
        <div
          class="orangehrm-attendance-card-stat orangehrm-attendance-card-stat--break"
        >
          <oxd-text tag="span" class="orangehrm-attendance-card-stat-label">
            {{ $t('attendance.break_time') }}
          </oxd-text>
          <oxd-text tag="span" class="orangehrm-attendance-card-stat-value">
            {{ formatTime(dayBreak) }}
          </oxd-text>
        </div>
        <div
          class="orangehrm-attendance-card-stat orangehrm-attendance-card-stat--net"
        >
          <oxd-text tag="span" class="orangehrm-attendance-card-stat-label">
            {{ $t('attendance.net_working_time') }}
          </oxd-text>
          <oxd-text tag="span" class="orangehrm-attendance-card-stat-value">
            {{ formatTime(dayNet) }}
          </oxd-text>
        </div>
      </div>
      <break-controls
        v-if="state === 'PUNCHED IN' || state === 'ON_BREAK'"
        compact
        :is-on-break="state === 'ON_BREAK'"
        :disabled="isLoading"
        class="orangehrm-attendance-card-break"
        @pause="onPause"
        @resume="onResume"
      />
      <div class="orangehrm-attendance-card-bar">
        <oxd-text tag="span" class="orangehrm-attendance-card-fulltime">
          <b>{{ dayNet.hours }}h</b> <b>{{ dayNet.minutes }}m</b>
          {{ $t('general.today') }}
        </oxd-text>
        <oxd-icon-button
          v-if="state !== 'PUNCHED IN' && state !== 'ON_BREAK'"
          name="stopwatch"
          display-type="solid-main"
          class="orangehrm-attendance-card-action"
          @click="onClickPunchIn"
        />
        <oxd-icon-button
          v-else
          name="stopwatch"
          display-type="ghost"
          class="orangehrm-attendance-card-action"
          @click="onClickPunchIn"
        />
      </div>
      <oxd-divider />
      <div class="orangehrm-attendance-card-summary">
        <div class="orangehrm-attendance-card-summary-week">
          <oxd-text tag="p">
            {{ $t('dashboard.this_week') }}
          </oxd-text>
          <oxd-text tag="p">
            {{ currentWeek }}
          </oxd-text>
          <oxd-text tag="p" class="orangehrm-attendance-card-subtime">
            {{ $t('attendance.net_working_time') }}: {{ formatTime(weekNet) }}
          </oxd-text>
        </div>
        <div class="orangehrm-attendance-card-summary-total">
          <oxd-icon name="stopwatch" class="orangehrm-attendance-card-icon" />
          <oxd-text tag="p" class="orangehrm-attendance-card-fulltime">
            {{ weekNet.hours }}h {{ weekNet.minutes }}m
          </oxd-text>
        </div>
      </div>
      <oxd-divider />
      <div class="orangehrm-attendance-card-month">
        <oxd-text tag="p" class="orangehrm-attendance-card-month-title">
          {{ $t('attendance.this_month') }}
        </oxd-text>
        <oxd-text tag="p" class="orangehrm-attendance-card-time-row">
          {{ $t('attendance.total_time') }}: {{ formatTime(monthGross) }}
        </oxd-text>
        <oxd-text tag="p" class="orangehrm-attendance-card-time-row">
          {{ $t('attendance.break_time') }}: {{ formatTime(monthBreak) }}
        </oxd-text>
        <oxd-text tag="p" class="orangehrm-attendance-card-time-row">
          {{ $t('attendance.net_working_time') }}:
          {{ formatTime(monthNet) }}
        </oxd-text>
      </div>
    </div>
    <oxd-bar-chart
      :grid="false"
      :data="dataset"
      :y-axsis="false"
      :aspect-ratio="false"
      wrapper-classes="emp-attendance-chart"
    ></oxd-bar-chart>
  </base-widget>
</template>

<script>
import {
  isToday,
  freshDate,
  parseDate,
  formatDate,
  guessTimezone,
} from '@/core/util/helper/datefns';
import {navigate} from '@/core/util/helper/navigation';
import useLocale from '@/core/util/composable/useLocale';
import {APIService} from '@/core/util/services/api.service';
import BaseWidget from '@/orangehrmDashboardPlugin/components/BaseWidget.vue';
import BreakControls from '@/orangehrmAttendancePlugin/components/BreakControls.vue';
import {OxdBarChart, OxdIcon, CHART_COLORS} from '@ohrm/oxd';

const emptyTime = () => ({hours: 0, minutes: 0});

export default {
  name: 'EmployeeAttendanceWidget',

  components: {
    'oxd-icon': OxdIcon,
    'base-widget': BaseWidget,
    'break-controls': BreakControls,
    'oxd-bar-chart': OxdBarChart,
  },

  setup() {
    const {locale} = useLocale();
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/dashboard/employees/time-at-work',
    );
    const breakHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/attendance/breaks',
    );

    return {
      http,
      breakHttp,
      locale,
    };
  },

  data() {
    return {
      dataset: [],
      state: null,
      endDate: null,
      userDate: null,
      userTime: null,
      startDate: null,
      isLoading: false,
      empNumber: null,
      timezoneOffset: null,
      dayGross: emptyTime(),
      dayBreak: emptyTime(),
      dayNet: emptyTime(),
      weekNet: emptyTime(),
      monthGross: emptyTime(),
      monthBreak: emptyTime(),
      monthNet: emptyTime(),
    };
  },

  computed: {
    lastState() {
      switch (this.state) {
        case 'PUNCHED IN':
          return this.$t('attendance.punched_in');
        case 'PUNCHED OUT':
          return this.$t('attendance.punched_out');
        case 'ON_BREAK':
          return this.$t('attendance.on_break');
        default:
          return this.$t('attendance.not_punched_in');
      }
    },
    lastRecord() {
      if (!this.userDate || !this.userTime) return null;
      const parsedDate = parseDate(
        `${this.userDate} ${this.userTime}`,
        'yyyy-MM-dd HH:mm',
      );
      const formattedTime = formatDate(parsedDate, 'hh:mm a', {
        locale: this.locale,
      });

      if (!isToday(parsedDate)) {
        const formattedDate = formatDate(parsedDate, 'MMM do', {
          locale: this.locale,
        });
        return this.$t('dashboard.state_date_at_time_timezone_offset', {
          lastState: this.lastState,
          date: formattedDate,
          time: formattedTime,
          timezoneOffset: this.timezoneOffset,
        });
      }

      return this.$t('dashboard.state_today_at_time_timezone_offset', {
        lastState: this.lastState,
        time: formattedTime,
        timezoneOffset: this.timezoneOffset,
      });
    },
    currentWeek() {
      if (!this.startDate || !this.endDate) return null;
      const startDate = formatDate(parseDate(this.startDate), 'MMM dd', {
        locale: this.locale,
      });
      const endDate = formatDate(parseDate(this.endDate), 'MMM dd', {
        locale: this.locale,
      });
      return `${startDate} - ${endDate}`;
    },
  },

  beforeMount() {
    this.fetchWidgetData();
  },

  methods: {
    formatTime(timeObj) {
      if (!timeObj) return '0h 0m';
      return `${timeObj.hours}h ${timeObj.minutes}m`;
    },
    getBreakPayload() {
      const currentDate = freshDate();
      const tz = guessTimezone();
      return {
        date: formatDate(currentDate, 'yyyy-MM-dd'),
        time: formatDate(new Date(), 'HH:mm'),
        timezoneOffset: tz.offset,
        timezoneName: tz.name,
      };
    },
    onClickPunchIn() {
      navigate('/attendance/punchIn');
    },
    onPause() {
      this.isLoading = true;
      this.breakHttp
        .create(this.getBreakPayload())
        .then(() => this.$toast.saveSuccess())
        .then(() => this.fetchWidgetData())
        .finally(() => {
          this.isLoading = false;
        });
    },
    onResume() {
      this.isLoading = true;
      this.breakHttp
        .request({
          method: 'PUT',
          data: this.getBreakPayload(),
        })
        .then(() => this.$toast.saveSuccess())
        .then(() => this.fetchWidgetData())
        .finally(() => {
          this.isLoading = false;
        });
    },
    fetchWidgetData() {
      this.isLoading = true;
      const currentDate = freshDate();
      const timezoneOffset = (currentDate.getTimezoneOffset() / 60) * -1;
      this.http
        .getAll({
          timezoneOffset,
          currentDate: formatDate(currentDate, 'yyyy-MM-dd'),
          currentTime: formatDate(new Date(), 'HH:mm'),
        })
        .then((response) => {
          const {data, meta} = response.data;
          this.dataset = data.map((item) => ({
            value: item.totalTime.hours + item.totalTime.minutes / 60,
            label: this.$t(
              `general.${new String(item.workDay.day).toLowerCase()}`,
            ),
            color: CHART_COLORS.COLOR_HEAT_WAVE,
          }));

          const {
            lastAction,
            currentDay,
            currentWeek,
            currentMonth,
            currentUser,
          } = meta;
          if (lastAction) {
            this.state = lastAction.state;
            this.userDate = lastAction.userDate;
            this.userTime = lastAction.userTime;
            this.timezoneOffset = lastAction.timezoneOffset;
          }
          if (currentWeek) {
            this.weekNet = currentWeek.netWorkingTime || currentWeek.totalTime;
            this.endDate = currentWeek.endDate?.date;
            this.startDate = currentWeek.startDate?.date;
          }
          if (currentDay) {
            this.dayGross = currentDay.totalTime || emptyTime();
            this.dayBreak = currentDay.breakTime || emptyTime();
            this.dayNet = currentDay.netWorkingTime || currentDay.totalTime;
          }
          if (currentMonth) {
            this.monthGross = currentMonth.totalTime || emptyTime();
            this.monthBreak = currentMonth.breakTime || emptyTime();
            this.monthNet = currentMonth.netWorkingTime || emptyTime();
          }
          if (currentUser) {
            this.empNumber = currentUser.empNumber;
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style src="./employee-attendance-widget.scss" lang="scss" scoped></style>
